import axios from "axios";

// Get the API base URL (without /api suffix)
// Always use the environment variable - it should be configured correctly for each environment
export const getApiUrl = () => {
  let envUrl = process.env.REACT_APP_BACKEND_URL;
  // Strip trailing /api if present to prevent double /api/api
  if (envUrl && envUrl.endsWith('/api')) {
    envUrl = envUrl.slice(0, -4);
  }
  // Strip trailing slash
  if (envUrl && envUrl.endsWith('/')) {
    envUrl = envUrl.slice(0, -1);
  }
  return envUrl || (typeof window !== 'undefined' ? window.location.origin : '');
};

// Create axios instance with credentials and dynamic base URL
const api = axios.create({
  withCredentials: true,
  baseURL: getApiUrl()
});

// Add interceptor to include auth token from localStorage
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('session_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Third-party integration paths that return 401 when their *external* token
// (NPSSO / Xbox Live / Steam / Exophase / etc.) expires. Those 401s must NOT
// kick the user to the app login screen — the user's app session is still
// valid; it's the *integration* that's expired. Callers (Settings, sync
// dialogs, etc.) are expected to surface their own toast/dialog for these.
const THIRD_PARTY_401_PATHS = [
  '/playstation/',
  '/psn/',
  '/xbox/',
  '/steam/',
  '/exophase/',
  '/gamefaqs/',
  '/igdb/',
  '/gog/',
  '/nintendo/',
];

const isThirdPartyIntegration401 = (url) => {
  if (!url) return false;
  return THIRD_PARTY_401_PATHS.some((p) => url.includes(p));
};

// Add response interceptor to handle 401 errors gracefully
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // If we get a 401 on any API call (except /auth/me which is used for initial check),
    // clear the token and redirect to login
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      const isAuthMeRequest = url.includes('/auth/me');
      const isIntegration = isThirdPartyIntegration401(url);
      if (!isAuthMeRequest && !isIntegration) {
        console.warn('Session expired or invalid. Redirecting to login.');
        localStorage.removeItem('session_token');
        // Only redirect if we're not already on the login page
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// Also set default for regular axios
axios.defaults.withCredentials = true;
axios.defaults.baseURL = getApiUrl();

// Add interceptor to regular axios as well
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('session_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add response interceptor to regular axios as well
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      const isAuthMeRequest = url.includes('/auth/me');
      const isIntegration = isThirdPartyIntegration401(url);
      if (!isAuthMeRequest && !isIntegration) {
        console.warn('Session expired or invalid. Redirecting to login.');
        localStorage.removeItem('session_token');
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

export default api;
